import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NyxtLogo } from '../components/NyxtLogo';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// Logger utility
const logger = {
  error: (message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }
};

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Auth card component
const AuthCard = ({ onGoogleLogin, onGuestLogin, loading }) => (
  <div 
    className="relative z-10 w-full max-w-md p-8 rounded-3xl shadow-lg"
    style={{ backgroundColor: '#FFFFFF', border: '1px solid #DCD7CE' }}
  >
    <div className="flex flex-col items-center mb-8">
      <NyxtLogo size={64} className="mb-4" />
      <h1 
        className="text-3xl font-medium tracking-tight"
        style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#1A1D1A' }}
      >
        NYXT AI
      </h1>
      <p className="mt-2 text-center" style={{ color: '#596059' }}>
        Your personal AI assistant for real-time knowledge and advice
      </p>
    </div>
    
    <div className="space-y-4">
      <Button
        onClick={onGoogleLogin}
        disabled={loading}
        data-testid="login-google-btn"
        className="
          w-full py-6 rounded-xl
          bg-white hover:bg-[#F9F8F6]
          text-[#1A1D1A] hover:text-[#1A1D1A]
          border border-[#DCD7CE]
          font-medium text-base
          transition-all duration-200
          btn-lift
        "
        variant="outline"
      >
        <GoogleIcon />
        Continue with Google
      </Button>
      
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t" style={{ borderColor: '#DCD7CE' }} />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="px-3" style={{ backgroundColor: '#FFFFFF', color: '#8A908A' }}>
            or
          </span>
        </div>
      </div>
      
      <Button
        onClick={onGuestLogin}
        disabled={loading}
        data-testid="guest-access-btn"
        className="
          w-full py-6 rounded-xl
          bg-transparent hover:bg-[#EBE8E1]
          text-[#596059] hover:text-[#1A1D1A]
          font-medium text-base
          transition-all duration-200
        "
        variant="ghost"
      >
        {loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
        Continue as Guest
      </Button>
    </div>
    
    <p className="mt-8 text-center text-xs" style={{ color: '#8A908A' }}>
      By continuing, you agree to our Terms of Service and Privacy Policy
    </p>
  </div>
);

export const AuthPage = () => {
  const { loginWithGoogle, loginAsGuest } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleGuestLogin = useCallback(async () => {
    setLoading(true);
    try {
      await loginAsGuest();
      navigate('/dashboard');
    } catch (error) {
      logger.error('Guest login failed:', error);
    } finally {
      setLoading(false);
    }
  }, [loginAsGuest, navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#F9F8F6' }}
    >
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1740324974165-d5a075315e43?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <AuthCard 
        onGoogleLogin={loginWithGoogle}
        onGuestLogin={handleGuestLogin}
        loading={loading}
      />
    </div>
  );
};

export default AuthPage;
